import { useState, useCallback } from "react";
import { useSelector } from "react-redux";
import useNotif from "src/hooks/useNotif";
import useRequest from "src/hooks/useRequest";
import useToggle from "src/hooks/useToggleV2";
import getCroppedImg from "./helper/cropImage";

function useImageUploader({ url, onClose, callBack = () => null }) {
  const { userToken } = useSelector(state => state.auth);
  const headers = {
    "content-type": "multipart/form-data",
    "x-access-tokens": userToken
  };
  const [upImg, setUpImg] = useState(null);
  const [imgName, setImgName] = useState("");
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedArea, setCroppedArea] = useState(null);

  const [completedCrop, setCompletedCrop] = useState(null);
  const [loading, loadingOn, loadingOff] = useToggle(false);
  const notify = useNotif();
  const request = useRequest(loadingOn, loadingOff, { headers });

  async function upload() {
    const croppedImage = await getCroppedImg(upImg, completedCrop);

    let formData = new FormData();

    const file = new File([croppedImage], imgName);
    formData.append("file", file);
    formData.append("name", "signature.png");
    formData.append("type", "image/png");

    const { success } = await request.post(url, formData);

    if (!success) notify.error("Failed to upload image.");
    else {
      // reload cache
      await request.get(`${url}/${imgName}`, {
        headers: {
          pragma: "no-cache",
          "cache-control": "no-cache"
        }
      });

      notify.success("Successfully uploaded! Please refresh your browser.");
      if (callBack) callBack(imgName);
      setUpImg(null);
      setImgName("");
      onClose();
    }
  }

  // Build a File object from the current crop without uploading
  async function buildFile() {
    const croppedImage = await getCroppedImg(upImg, completedCrop);
    const file = new File([croppedImage], imgName);
    return file;
  }

  function onChangeFile(file) {
    const reader = new FileReader();
    reader.addEventListener("load", () => setUpImg(reader.result));
    reader.readAsDataURL(file);
  }

  function onCrop(val) {
    setCrop(val);
  }

  function onZoom(val) {
    setZoom(val);
  }

  function onSetCropArea(val) {
    setCroppedArea(val);
  }

  function onClear() {
    setImgName("");
    setUpImg(null);
  }

  const onCropComplete = useCallback(
    (croppedArea, croppedAreaPixels) => {
      setCompletedCrop(croppedAreaPixels);
    },
    [completedCrop]
  );

  return {
    upload,
    crop,
    zoom,
    loading,
    upImg,
    imgName,
    croppedArea,
    completedCrop,
    onZoom,
    onCrop,
    onClear,
    setImgName,
    onChangeFile,
    onSetCropArea,
    onCropComplete,
    buildFile
  };
}

export default useImageUploader;
